"use client";

import React from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function JoinForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center border border-border/50 bg-background/40 backdrop-blur-md rounded-xl animate-in fade-in zoom-in duration-500">
                <p className="text-foreground font-default text-xl font-medium">{successMessage}</p>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="space-y-5 text-left">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-sm font-medium text-foreground">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Alex Reed" {...field} className="h-11 border-border focus-visible:ring-primary/50" />
                            </FormControl>
                            <p className="text-[11px] text-muted-foreground mt-1.5">How should we address you?</p>
                            <FormMessage className="text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-sm font-medium text-foreground">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="alex@example.com" {...field} className="h-11 border-border focus-visible:ring-primary/50" />
                            </FormControl>
                            <FormMessage className="text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="interests" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-sm font-medium text-foreground">Primary Interest</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <select {...field} className="flex h-11 w-full min-w-0 appearance-none rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 text-foreground">
                                        <option value="" disabled className="bg-background text-muted-foreground">Choose a category</option>
                                        <option value="Wellness" className="bg-background text-foreground">Wellness & Intimacy</option>
                                        <option value="Guides" className="bg-background text-foreground">Expert Guides</option>
                                        <option value="Zodiac" className="bg-background text-foreground">Zodiac & Love</option>
                                        <option value="All" className="bg-background text-foreground">The Full Playbook</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                                        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage className="text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="agree_terms" rules={{
        required: "You must accept to join the community"
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
                            <FormControl>
                                <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-sm border border-input bg-transparent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 accent-primary" checked={field.value || false} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                            </FormControl>
                            <div className="space-y-1.5 leading-none">
                                <FormLabel className="text-sm font-medium text-foreground leading-snug cursor-pointer">
                                    I agree to the privacy policy and editorial updates
                                </FormLabel>
                                <p className="text-[11px] text-muted-foreground">Unsubscribe at any time with one click.</p>
                            </div>
                            <FormMessage className="text-xs text-destructive" />
                        </FormItem>} />

                {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md mt-4">
                        <p className="text-sm text-destructive">{submitError}</p>
                    </div>}

                <Button type="submit" disabled={isSubmitting} className="w-full mt-8 h-11 text-base font-medium tracking-wide bg-primary text-primary-foreground hover:scale-105 transition-transform duration-300 rounded-full">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-4" />
                            Subscribing...
                        </> : 'Subscribe to The Playbook'}
                </Button>
            </div>
        </Form>;
}
export default function JoinTheCommunity() {
  return <section data-section-id="221498" id="join-the-community" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden flex flex-col items-center justify-center min-h-[80vh]">
            {}
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none mix-blend-overlay" style={{
      backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
    }} />

            <div className="max-w-xl w-full mx-auto relative z-10">
                <div className="text-center mb-10">
                    <h2 className="font-default text-4xl md:text-6xl font-bold tracking-tighter mb-4 text-foreground">
                        Intimacy, Inboxed.
                    </h2>
                    <p className="text-muted-foreground text-lg font-default max-w-md mx-auto leading-relaxed">
                        Join our inner circle for curated wellness guides, expert advice, and the latest editorial edits.
                    </p>
                </div>

                <div className="bg-card/40 backdrop-blur-2xl border border-border/30 rounded-2xl p-6 md:p-10 shadow-4xl relative">
                    {}
                    <div className="absolute -inset-0.5 bg-gradient-to-b from-primary/10 to-transparent rounded-2xl blur-md -z-10" />
                    
                    <FormProvider formKey="join_community_form_home" formId="join_community_form_home" sectionName="Join the Community" defaultValues={{
          name: "",
          email: "",
          interests: "",
          agree_terms: false
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          agree_terms: {
            required: true
          }
        }} submitText="Subscribe to The Playbook" successMessage="Welcome to the inner circle. Check your inbox for your first guide." className="w-full">
                        <JoinForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}